import { ApplicationConfig, provideZoneChangeDetection, isDevMode, provideAppInitializer, inject, LOCALE_ID } from '@angular/core';
import { provideRouter, Router } from '@angular/router';
import { KeycloakInitOptions } from 'keycloak-js';
import { routes } from './app.routes';
import { provideKeycloakAndInterceptor } from './init/keycloakConfiguration';
import { AuthSyncService } from './services/auth-sync.service';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { includeBearerTokenInterceptor } from 'keycloak-angular';
import { loggingInterceptor } from './interceptors/logging.interceptor';
import { clientVersionInterceptor } from './interceptors/client-version.interceptor';
import { UpdateCheckService } from './services/update-check.service';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { appReducers } from './store/reducers/app.reducers';
import { CompanyFormEffects } from './store/effects/company-form.effects';
import { LookupDataService } from './services/lookupDataService.service';
import { ActiveYearService } from './services/active-year.service';
import { CountryFlat } from './models/country';
import { Currency } from './models/currency';
import { Helpers } from './utilities/helpers';
import { provideHotToastConfig } from '@ngxpert/hot-toast';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, NativeDateAdapter } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { CurrentUserState } from './state/current-user.state';
import { AppUserRole } from './models/enums/appUserRole.enum';
import { catchError, of, firstValueFrom } from 'rxjs';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';

// Custom date formats (Greek format with English locale)
export const CUSTOM_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'DD/MM/YYYY',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

export const createAppConfig = (keycloakInitOptions: KeycloakInitOptions): ApplicationConfig => ({
  providers: [
      provideKeycloakAndInterceptor(keycloakInitOptions),
      // Eagerly construct the cross-tab auth responder so this tab can hand its
      // tokens to the next tab that opens (avoiding a redundant Keycloak login).
      provideAppInitializer(() => {
        inject(AuthSyncService);
      }),
      provideHttpClient(
        withInterceptorsFromDi(),
        withInterceptors([
          includeBearerTokenInterceptor,
          loggingInterceptor,  // Add logging after bearer token to verify it was added
          clientVersionInterceptor  // Stamp X-Client-Version and handle 426 (stale client)
        ])
      ),
      // Watch for newer frontend deployments and surface the reload banner.
      provideAppInitializer(() => {
        inject(UpdateCheckService).start();
      }),
      // Load current user first (this waits for Keycloak token)
      provideAppInitializer(() => {
        const currentUserState = inject(CurrentUserState);
        const router = inject(Router);

        return firstValueFrom(
          currentUserState.loadCurrentUser().pipe(
            catchError(error => {
              console.error('Failed to load current user:', error);

              // Determine error message based on error type
              let errorMessage = 'Unable to load user permissions. Please try again.';
              let errorTitle = 'Error Loading Permissions';

              if (error.message?.includes('Timeout waiting for Keycloak token')) {
                errorTitle = 'Authentication Timeout';
                errorMessage = 'Authentication took too long. Please refresh the page to try again.';
              } else if (error.status === 401) {
                errorTitle = 'Authentication Required';
                errorMessage = 'Your session has expired. Please log in again.';
              } else if (error.status === 403) {
                errorTitle = 'Access Denied';
                errorMessage = 'You do not have permission to access this application.';
              } else if (error.status === 404) {
                errorTitle = 'User Not Found';
                errorMessage = 'Your user account could not be found. Please contact your administrator.';
              } else if (error.status >= 500) {
                errorTitle = 'Server Error';
                errorMessage = 'The server encountered an error. Please try again later or contact support.';
              } else if (!navigator.onLine) {
                errorTitle = 'No Internet Connection';
                errorMessage = 'Please check your internet connection and try again.';
              }

              // Navigate to error page with error details
              setTimeout(() => {
                router.navigate(['/error'], {
                  state: {
                    title: errorTitle,
                    message: errorMessage
                  }
                });
              }, 0);

              // Return null to allow app to continue (error page will be shown)
              return of(null);
            })
          )
        );
      }),
      // Load lookup tables and the active year after current user is loaded.
      // The two app initializers run in parallel via Promise.all, so we explicitly
      // wait on the `loaded` signal here — otherwise a customer whose /me call hasn't
      // resolved yet would slip past the role check and fire every admin LUT.
      provideAppInitializer(async () => {
        const lookupDataService = inject(LookupDataService);
        const activeYearService = inject(ActiveYearService);
        const currentUserState = inject(CurrentUserState);

        while (!currentUserState.loaded()) {
          await new Promise(r => setTimeout(r, 50));
        }

        if (currentUserState.user()?.role === AppUserRole.Customer) {
          return;
        }

        await Promise.all([
          activeYearService.bootstrap()
            .then(() => Helpers.setActiveYearAccessor(() => activeYearService.current()))
            .catch(error => console.error('Failed to load active year:', error)),
          lookupDataService.loadTable<CountryFlat>('countries'),
          lookupDataService.loadTable<Currency>('currencies'),
          lookupDataService.loadTable('nationalities'),
          lookupDataService.loadTable('professions'),
          lookupDataService.loadTable('honorificTitles'),
          lookupDataService.loadTable('qualifications'),
          lookupDataService.loadTable('financialExpertises'),
          lookupDataService.loadTable('politicalPositions'),
          lookupDataService.loadTable('industryExpertises'),
          lookupDataService.loadTable('stockExchanges'),
          lookupDataService.loadTable('stockIndices'),
          lookupDataService.loadTable('industries'),
          lookupDataService.loadTable('boardMemberOccupations')
        ]);
      }),
      provideZoneChangeDetection({ eventCoalescing: true }),
      provideRouter(routes),
      provideAnimationsAsync(),
      provideHotToastConfig(),
      provideCharts(withDefaultRegisterables()),
      provideStore(appReducers),
      provideEffects([CompanyFormEffects]),
      provideStoreDevtools({
        maxAge: 25,
        logOnly: !isDevMode(),
        autoPause: true,
        trace: false,
        traceLimit: 75,
    }),
    // British English locale (uses DD/MM/YYYY format)
    { provide: LOCALE_ID, useValue: 'en-GB' },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS }
  ]
});
